import { Group, Icon, Modal, SelectionCard, Text } from '@melio/penny';

import type { AuthenticatorData } from '../../api/auth';

// Helper functions for authenticator display
export const getAuthenticatorLabel = (authenticator: AuthenticatorData): string => {
  switch (authenticator.type) {
    case 'sms':
      return 'Text message';
    case 'email':
      return 'Email verification';
    default: {
      const _exhaustiveCheck: never = authenticator.type;
      return _exhaustiveCheck;
    }
  }
};

export const getAuthenticatorIcon = (authenticator: AuthenticatorData): 'email' | 'phone' => {
  switch (authenticator.type) {
    case 'sms':
      return 'phone';
    case 'email':
      return 'email';
    default: {
      const _exhaustiveCheck: never = authenticator.type;
      return _exhaustiveCheck;
    }
  }
};

interface AuthenticatorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  authenticators: AuthenticatorData[];
  currentAuthenticatorId: string | null;
  selectedAuthenticator: AuthenticatorData | null;
  onSelectAuthenticator: (authenticator: AuthenticatorData) => void;
}

export const AuthenticatorSelectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  authenticators,
  currentAuthenticatorId,
  selectedAuthenticator,
  onSelectAuthenticator,
}: AuthenticatorSelectionModalProps) => {
  // Filter out the current authenticator to show only alternatives
  const alternativeAuthenticators = authenticators.filter((auth) => auth.id !== currentAuthenticatorId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Verify your account"
      size="small"
      data-testid="authenticator-modal"
      primaryButton={{
        label: 'Continue',
        onClick: onConfirm,
        isLoading,
        variant: 'primary',
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: onClose,
        variant: 'tertiary',
      }}
    >
      <Group variant="vertical" spacing="m">
        <Text textStyle="body2" color="semantic.text.primary">
          Choose authentication method
        </Text>
        <Group variant="vertical" spacing="s">
          {alternativeAuthenticators.map((authenticator) => (
            <SelectionCard
              key={authenticator.id}
              mainLabelProps={{ label: getAuthenticatorLabel(authenticator) }}
              leftElement={<Icon type={getAuthenticatorIcon(authenticator)} />}
              selected={selectedAuthenticator?.id === authenticator.id}
              onClick={() => onSelectAuthenticator(authenticator)}
              data-testid={`authenticator-option-${authenticator.type}`}
            />
          ))}
        </Group>
      </Group>
    </Modal>
  );
};
